import { useCallback } from "react";
import {
  login as apiLogin,
  logout as apiLogout,
  me as apiMe,
  refresh as apiRefresh,
} from "@/features/auth/api";
import { useAuthStore } from "@/store/auth";
import { clearGuestSession } from "@/hooks/useGuestSession";
import { requestCartResync } from "@/features/cart/useCartSync";

export function useAuth() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const organization = useAuthStore((s) => s.organization);
  const hydrated = useAuthStore((s) => s.hydrated);

  const setSession = useAuthStore((s) => s.setSession);
  const setUser = useAuthStore((s) => s.setUser);
  const setOrganization = useAuthStore((s) => s.setOrganization);
  const markHydrated = useAuthStore((s) => s.markHydrated);
  const logoutLocal = useAuthStore((s) => s.logoutLocal);

  const login = useCallback(
    async (email: string, password: string, totp?: string) => {
      // apiLogin ya envía guestSessionId al backend para triggear el merge
      const session = await apiLogin(email, password, totp);
      setSession(session);
      markHydrated();

      // Post-auth: limpiar guest identity y forzar re-fetch del cart merged
      clearGuestSession();
      requestCartResync();

      return session;
    },
    [markHydrated, setSession],
  );

  const refresh = useCallback(async () => {
    const session = await apiRefresh();

    if (!session) {
      logoutLocal();
      markHydrated();
      return null;
    }

    setSession(session);
    markHydrated();
    return session.accessToken;
  }, [logoutLocal, markHydrated, setSession]);

  const syncMe = useCallback(async () => {
    const result = await apiMe();
    setUser(result.customer);
    setOrganization(result.organization);
    markHydrated();
    return result.customer;
  }, [markHydrated, setOrganization, setUser]);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      logoutLocal();
      markHydrated();
    }
  }, [logoutLocal, markHydrated]);

  return {
    accessToken,
    user,
    organization,
    isAuthenticated: Boolean(accessToken && user),
    loading: !hydrated,
    login,
    logout,
    refresh,
    syncMe,
  };
}
